import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Trash,
  Minus,
  Plus,
  ShoppingCartSimple,
  Receipt,
  CircleNotch,
  Ticket,
  X,
} from "@phosphor-icons/react";
import api, { formatCurrency } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SHIPPING_FIELDS = [
  { key: "shipping_name", label: "Full Name / Institution", placeholder: "Dr. Jane Researcher", col: "sm:col-span-2" },
  { key: "shipping_address", label: "Address", placeholder: "100 Research Park Dr", col: "sm:col-span-2" },
  { key: "shipping_city", label: "City", placeholder: "Cambridge", col: "" },
  { key: "shipping_state", label: "State / Region", placeholder: "MA", col: "" },
  { key: "shipping_zip", label: "ZIP / Postal Code", placeholder: "02139", col: "" },
  { key: "shipping_country", label: "Country", placeholder: "USA", col: "" },
];

export default function Cart() {
  const { items, updateQuantity, removeItem, total, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount }
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [shipping, setShipping] = useState({
    shipping_name: "",
    shipping_address: "",
    shipping_city: "",
    shipping_state: "",
    shipping_zip: "",
    shipping_country: "USA",
  });

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setApplyingCoupon(true);
    setCouponError("");
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal: total });
      setAppliedCoupon({ code: data.code, discount: data.discount });
      toast.success(`Coupon ${data.code} applied`);
    } catch (e) {
      setAppliedCoupon(null);
      setCouponError(e.response?.data?.detail || "Invalid coupon");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError("");
  };

  const discount = appliedCoupon ? Math.min(appliedCoupon.discount, total) : 0;
  const grandTotal = Math.max(total - discount, 0);

  const handleRequestInvoice = async () => {
    if (!user) {
      toast.error("Please sign in to complete your order");
      navigate("/login", { state: { from: "/cart" } });
      return;
    }
    const required = ["shipping_name", "shipping_address", "shipping_city", "shipping_zip"];
    if (required.some((k) => !shipping[k].trim())) {
      toast.error("Please complete the shipping details");
      return;
    }
    setLoading(true);
    try {
      await api.post("/orders/invoice", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        coupon_code: appliedCoupon?.code || null,
        ...shipping,
      });
      clearCart();
      toast.success("Invoice requested — check your email for payment instructions.");
      navigate("/account");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit your order. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0)
    return (
      <div className="mx-auto max-w-2xl px-6 py-28 text-center" data-testid="empty-cart">
        <ShoppingCartSimple size={56} weight="duotone" className="mx-auto text-cyan/60" />
        <h1 className="mt-6 font-heading text-3xl font-bold text-white">Your cart is empty</h1>
        <p className="mt-3 text-sm text-teal-muted">Add research compounds to get started.</p>
        <Link
          to="/shop"
          data-testid="empty-cart-shop-btn"
          className="mt-8 inline-block bg-cyan px-7 py-3 font-mono text-sm font-semibold uppercase tracking-wider text-navy-deep transition-all hover:bg-cyan-bright hover:shadow-glow"
        >
          Browse Catalog
        </Link>
      </div>
    );

  return (
    <div data-testid="cart-page" className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:px-12">
      <h1 className="font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">Cart</h1>
      <p className="mt-2 font-mono text-xs uppercase tracking-widest text-teal">
        {items.length} {items.length === 1 ? "item" : "items"}
      </p>

      <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_380px]">
        {/* Items + shipping */}
        <div className="space-y-8">
          <div className="divide-y divide-cyan/10 border border-cyan/15 bg-navy-surface">
            {items.map((item) => (
              <div key={item.product_id} data-testid={`cart-item-${item.product_id}`} className="flex gap-4 p-5">
                <Link to={`/product/${item.product_id}`} className="h-24 w-24 shrink-0 border border-cyan/15 bg-navy-deep">
                  <img src={item.image_url} alt={item.name} className="h-full w-full object-cover" />
                </Link>
                <div className="flex flex-1 flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Link to={`/product/${item.product_id}`} className="font-heading text-base font-bold text-white hover:text-cyan">
                        {item.name}
                      </Link>
                      <p className="font-mono text-[11px] uppercase tracking-wider text-teal">{item.category}</p>
                    </div>
                    <button
                      data-testid={`cart-remove-${item.product_id}`}
                      onClick={() => removeItem(item.product_id)}
                      className="text-teal-muted transition-colors hover:text-destructive"
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                  <div className="mt-auto flex items-center justify-between pt-3">
                    <div className="flex items-center border border-cyan/20">
                      <button
                        data-testid={`cart-decrease-${item.product_id}`}
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="flex h-9 w-9 items-center justify-center text-teal-muted hover:text-cyan"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-10 text-center font-mono text-sm text-white">{item.quantity}</span>
                      <button
                        data-testid={`cart-increase-${item.product_id}`}
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="flex h-9 w-9 items-center justify-center text-teal-muted hover:text-cyan"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <span className="font-mono text-base font-bold text-white">
                      {formatCurrency(item.price * item.quantity)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Shipping */}
          <div className="border border-cyan/15 bg-navy-surface p-6">
            <h2 className="mb-5 font-mono text-xs uppercase tracking-widest text-cyan">// Shipping Details</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {SHIPPING_FIELDS.map((f) => (
                <div key={f.key} className={f.col}>
                  <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-teal">
                    {f.label}
                  </label>
                  <input
                    data-testid={`input-${f.key}`}
                    value={shipping[f.key]}
                    onChange={(e) => setShipping((s) => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="w-full border border-cyan/15 bg-navy px-3 py-2.5 font-mono text-sm text-white placeholder:text-teal/40 outline-none transition-colors focus:border-cyan/50"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary */}
        <aside className="h-fit border border-cyan/20 bg-navy-surface p-6 lg:sticky lg:top-24">
          <h2 className="font-mono text-xs uppercase tracking-widest text-cyan">// Order Summary</h2>

          {/* Coupon */}
          <div className="mt-5">
            {appliedCoupon ? (
              <div className="flex items-center justify-between border border-cyan/30 bg-cyan/5 px-3 py-2.5" data-testid="applied-coupon">
                <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-cyan">
                  <Ticket size={15} weight="fill" /> {appliedCoupon.code}
                </span>
                <button
                  data-testid="remove-coupon-button"
                  onClick={removeCoupon}
                  className="text-teal-muted transition-colors hover:text-destructive"
                >
                  <X size={15} />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  data-testid="coupon-input"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  placeholder="Coupon code"
                  className="w-full border border-cyan/15 bg-navy px-3 py-2.5 font-mono text-xs uppercase tracking-wider text-white placeholder:text-teal/40 outline-none focus:border-cyan/50"
                />
                <button
                  data-testid="apply-coupon-button"
                  onClick={applyCoupon}
                  disabled={applyingCoupon}
                  className="shrink-0 border border-cyan/30 px-4 font-mono text-xs uppercase tracking-wider text-cyan transition-colors hover:bg-cyan/10 disabled:opacity-60"
                >
                  {applyingCoupon ? <CircleNotch size={14} className="animate-spin" /> : "Apply"}
                </button>
              </div>
            )}
            {couponError && (
              <p data-testid="coupon-error" className="mt-2 font-mono text-[11px] text-destructive">{couponError}</p>
            )}
          </div>

          <div className="mt-5 space-y-3 border-b border-cyan/10 pb-5">
            <div className="flex justify-between font-mono text-sm text-teal-muted">
              <span>Subtotal</span>
              <span className="text-white">{formatCurrency(total)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between font-mono text-sm text-cyan" data-testid="discount-line">
                <span>Discount ({appliedCoupon.code})</span>
                <span>- {formatCurrency(discount)}</span>
              </div>
            )}
            <div className="flex justify-between font-mono text-sm text-teal-muted">
              <span>Shipping</span>
              <span className="text-cyan">Calculated at fulfillment</span>
            </div>
          </div>
          <div className="flex items-baseline justify-between py-5">
            <span className="font-mono text-sm uppercase tracking-widest text-white">Total</span>
            <span data-testid="cart-total" className="font-mono text-2xl font-bold text-cyan">
              {formatCurrency(grandTotal)}
            </span>
          </div>
          <button
            data-testid="checkout-button"
            onClick={handleRequestInvoice}
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 bg-cyan py-3.5 font-mono text-sm font-semibold uppercase tracking-wider text-navy-deep transition-all duration-300 hover:bg-cyan-bright hover:shadow-glow disabled:opacity-60"
          >
            {loading ? (
              <>
                <CircleNotch size={18} className="animate-spin" /> Submitting…
              </>
            ) : (
              <>
                <Receipt size={16} weight="fill" /> Request Invoice
              </>
            )}
          </button>
          <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-wider text-teal/70">
            We'll email an invoice with payment instructions · ships once payment is confirmed
          </p>
          {!user && (
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-wider text-cyan">
              Sign in required to checkout
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
